import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useStytchB2BClient } from '@stytch/react/b2b';
import { useNavigate } from 'react-router';
import { z } from 'zod';

import { businessEmail, useAppForm } from '@/components/Form';
import { FieldLabel } from '@/components/Form/fields';
import { maxEmailLength, sessionDurationMinutes } from '@/constants/numbers';
import { getStytchAPIErrorMessage, isStytchAPIError } from '@/error/StytchError';
import { useNotification } from '@/providers/Notification';
import { ROUTES } from '@/router';

import RateLimit from './RateLimitButton';

type PasswordSignInFormProps = {
  organizationId?: string;
  redirectUrl?: string;
  onSuccess?: () => void;
  onStartAuth?: () => void;
};

/**
 * Reusable password sign-in form component.
 * - If organizationId is provided, uses tenant-specific password auth
 * - If organizationId is not provided, uses discovery flow
 */
const PasswordSignInForm = ({
  organizationId,
  redirectUrl,
  onSuccess,
  onStartAuth,
}: PasswordSignInFormProps) => {
  const navigate = useNavigate();
  const stytch = useStytchB2BClient();
  const { pushNotification } = useNotification();
  const formSchema = z.object({
    email: businessEmail(),
    password: z.string().min(1, t`Password is required`),
  });

  const form = useAppForm({
    defaultValues: {
      email: '',
      password: '',
    },
    validators: {
      onSubmit: formSchema,
      onSubmitAsync: async ({ value: { email, password } }) => {
        onStartAuth?.();

        try {
          if (organizationId) {
            // Tenant-specific password authentication
            await stytch.passwords.authenticate({
              email_address: email,
              password,
              organization_id: organizationId,
              session_duration_minutes: sessionDurationMinutes,
            });
            onSuccess?.();
            navigate(ROUTES.app.path, { replace: true });
          } else {
            // Discovery flow for password authentication
            const { intermediate_session_token, email_address, discovered_organizations } =
              await stytch.passwords.discovery.authenticate({
                email_address: email,
                password,
              });

            onSuccess?.();
            navigate(ROUTES.signInDiscovery.path, {
              replace: true,
              state: {
                intermediateToken: intermediate_session_token,
                email: email_address,
                discoveredOrgs: discovered_organizations,
                isDiscoverySignUp: false,
                redirectUrl,
              },
            });
          }
        } catch (e) {
          if (isStytchAPIError(e)) {
            if (e.error_type === 'unauthorized_credentials' || e.error_type === 'email_not_found') {
              return {
                fields: {
                  password: [
                    {
                      message: t`Sorry, you entered an incorrect email address or password.`,
                    },
                  ],
                },
              };
            } else {
              pushNotification({
                type: 'error',
                message: getStytchAPIErrorMessage(e),
              });
            }
          }
        }
      },
    },
  });

  return (
    <form
      className="flex flex-col gap-7"
      onSubmit={(event) => {
        event.preventDefault();
        form.handleSubmit();
      }}
    >
      <form.AppField name="email">
        {(field) => (
          <field.Field>
            <FieldLabel htmlFor="password-sign-in-email">
              <Trans>Business email</Trans>
            </FieldLabel>
            <field.TextInput
              id="password-sign-in-email"
              inputMode="email"
              placeholder={t`name@work-email.com`}
              autoComplete="email"
              spellCheck={false}
              autoCapitalize="none"
              autoFocus
              maxLength={maxEmailLength}
              size="large"
            />
            <field.Error />
          </field.Field>
        )}
      </form.AppField>

      <form.AppField name="password">
        {(field) => (
          <field.Field>
            <FieldLabel htmlFor="sign-in-password">
              <Trans>Password</Trans>
            </FieldLabel>
            <field.PasswordInput
              id="sign-in-password"
              placeholder={t`Password`}
              spellCheck={false}
              autoCapitalize="none"
              size="large"
            />
            <field.Error />
          </field.Field>
        )}
      </form.AppField>

      <form.AppForm>
        <RateLimit>
          <form.SubmitButton variant="primary" size="large" block>
            <Trans>Sign in</Trans>
          </form.SubmitButton>
        </RateLimit>
      </form.AppForm>
    </form>
  );
};

export default PasswordSignInForm;
